<template>
  <VDialogV3
    class="shipment-cargo-unit-dialog"
    position="right"
    width="600px"
    :entity="localCargoUnit"
    ref="form"
    hide-overlay-blur
  >
    <!-- Режим импорта -->
    <template v-if="isImporting">
      <VInput
        placeholder="Ctrl + V сюда выделенный диапазон"
        @paste="onImport($event)"
      />
    </template>

    <!-- Форма -->
    <template v-else>
      <section>
        <!-- Тип габаритов -->
        <VSelect
          v-model="localCargoUnit.selectedSizeType"
          label="Тип габаритов"
          layout="vertical"
          required
          :clearable="false"
          :options="sizeTypeList"
        />

        <!-- Стандартный паллет -->
        <template v-if="isStandardSize">
          <VSelect
            v-model="localCargoUnit.palletType"
            label="Размер паллета"
            layout="vertical"
            option-identity="id"
            required
            :clearable="false"
            :options="palletTypeList"
          />
        </template>

        <!-- Индивидуальные размеры -->
        <template v-if="isIndividualSize">
          <VInputNumberRusV2
            v-model="localCargoUnit.length"
            :type="Dimension"
            label="Длина паллета, мм"
            layout="vertical"
            required
            :min="50"
            :max="2500"
          />

          <VInputNumberRusV2
            v-model="localCargoUnit.width"
            :type="Dimension"
            label="Ширина паллета, мм"
            layout="vertical"
            required
            :min="50"
            :max="2500"
          />
        </template>

        <!-- Высота -->
        <VInputNumberRusV2
          v-model="localCargoUnit.height"
          :type="Dimension"
          label="Высота груза с паллетом, мм"
          layout="vertical"
          required
          :min="50"
          :max="2400"
        />

        <!-- Объём одного места (вычисляемое поле) -->
        <VInputNumberRusV2
          label="Объём одного паллетоместа, м³"
          layout="vertical"
          disabled
          :type="Volume"
          v-model="localCargoUnit.volume"
        />

        <!-- Вес нетто одного места -->
        <VInputNumberRusV2
          v-model="localCargoUnit.weightNet"
          :type="Weight"
          label="Вес одного паллетоместа нетто, кг"
          layout="vertical"
          required
          :min="0.001"
          :max="1100"
        >
          <template #helper-text>
            Вес одного паллетоместа брутто: {{ localCargoUnit.weightGross?.format() }} кг
          </template>
        </VInputNumberRusV2>

        <!-- Количество -->
        <VInputNumberRusV2
          v-model="localCargoUnit.qty"
          :type="Qty"
          label="Кол-во паллетомест"
          layout="vertical"
          required
          :min="1"
          :max="999"
        />

        <!-- Общий объём (вычисляемое поле) -->
        <VInputNumberRusV2
          label="Общий объём, м³"
          layout="vertical"
          disabled
          :type="Volume"
          v-model="localCargoUnit.totalVolume"
        />

        <!-- Общий вес нетто (вычисляемое поле) -->
        <VInputNumberRusV2
          label="Общий вес нетто, кг"
          layout="vertical"
          disabled
          :type="Weight"
          v-model="localCargoUnit.totalWeightNet"
        >
          <template #helper-text>
            Общий вес брутто: {{ localCargoUnit.totalWeightGross?.format() }} кг
          </template>
        </VInputNumberRusV2>
      </section>
    </template>

    <template #footer>
      <VButton
        theme="primary"
        @click="submit"
      >
        Сохранить
      </VButton>

      <div class="right">
      <!-- Удалить — только в режиме редактирования: паллетоместо пришло пропом.
           Отнесена вправо, подальше от «Сохранить» -->
      <VButton
        v-if="cargoUnit !== null"
        class="remove-button"
        @click="remove"
      >
        Удалить
      </VButton>

      <VButton @click="$core.dialogs.CargoUnitCard.hide()">
        Отмена
      </VButton>
    </div>
    </template>
  </VDialogV3>
</template>

<style lang="scss">
.shipment-cargo-unit-dialog {

  footer {
    /** Удалить — в противоположный от «Сохранить» край футера */
    .right {
      margin-left: auto;

      > .mds-btn {
        margin-left: 20px;
      }
    }
  }

}
</style>

<script lang="ts">

import Core                          from '@/core/Core';
import CargoUnit                     from '@/domains/CargoUnits/CargoUnit';
import CargoUnitsService             from '@/domains/CargoUnits/CargoUnitsService';
import Dimension                     from '@/core/types/Dimension';
import Volume                        from '@/core/types/Volume';
import Weight                        from '@/core/types/Weight';
import Qty                           from '@/core/types/Qty';

import type AbstractShipmentDocument from '@/domains/Abstract/AbstractShipmentDocument';
import type TransportOrder           from '@/domains/TransportOrders/TransportOrder';

export default defineComponent({
  name: 'CargoUnitCard',

  props: {
    /** Паллетоместо для редактирования; null — создание нового (инициализирует карточка) */
    cargoUnit: {
      type: Object as PropType<CargoUnit | null>,
      default: null,
    },
    /** Владелец паллетомест — карточка сама кладёт в него результат */
    cargoUnitsOwner: {
      type: Object as PropType<AbstractShipmentDocument | TransportOrder>,
      required: true,
    },
    isImport: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'close',
  ],

  data() {
    return {
      localCargoUnit: undefined as unknown as CargoUnit,
      originalCompositeKey: null as string | null,

      sizeTypeList: CargoUnitsService.sizeTypeList(),
      palletTypeList: CargoUnitsService.getPalletTypeListForSelect(),

      imported: null as boolean | null,
    };
  },

  setup() {
    return {
      Dimension,
      Volume,
      Weight,
      Qty,
    };
  },

  computed: {
    /** Стандартный размер паллета */
    isStandardSize(): boolean {
      return this.localCargoUnit.selectedSizeType.id === 'standard';
    },

    /** Индивидуальные размеры -->  */
    isIndividualSize(): boolean {
      return this.localCargoUnit.selectedSizeType.id === 'individual';
    },

    /** Режим импорта — показываем поле для вставки, пока не вставили */
    isImporting(): boolean {
      return this.isImport === true && !this.imported;
    },
  },

  created() {
    this.init();
  },

  methods: {
    init(): void {
      if (this.cargoUnit === null) {
        this.localCargoUnit = new CargoUnit();
      } else {
        this.localCargoUnit = Core.utils.cloneDeep(this.cargoUnit);
        /** Фиксируем compositeKey ДО редактирования — карточка мутирует копию (localCargoUnit),
         * а compositeKey зависит от изменяемых полей. Нужен для updateCargoUnit, чтобы найти
         * и заменить старую запись в словаре, а не создать рядом новую под новым ключом.*/
        this.originalCompositeKey = this.cargoUnit.compositeKey;
      }
    },

    /** Сохранение — создание или обновление */
    async submit(): Promise<void> {
      const isValid = await (this.$refs.form as any).validate();

      if (!isValid) {
        return;
      }

      /** Редактирование определяем по пропу, а не по isExisting: у паллетомест
       *  несохранённого документа id ещё нет, и isExisting вернёт false */
      if (this.cargoUnit !== null) {
        this.cargoUnitsOwner.updateCargoUnit(this.localCargoUnit, this.originalCompositeKey);
      } else {
        this.cargoUnitsOwner.addCargoUnit(this.localCargoUnit);
      }

      this.$core.dialogs.CargoUnitCard.resolve();
    },

    /** Удаление паллетоместа */
    remove(): void {
      this.cargoUnitsOwner.removeCargoUnit(this.cargoUnit);
      this.$core.dialogs.CargoUnitCard.resolve();
    },

    /** Импорт из буфера обмена */
    onImport(event: ClipboardEvent): void {
      if (!(event instanceof ClipboardEvent)) { return; }

      const dataRaw = event.clipboardData.getData('text');

      const qty    = dataRaw.match(/(\d+)\s*паллет/i);
      const weight = dataRaw.match(/(\d+)\s*кг/i);

      this.localCargoUnit.weightNet  = weight ? Weight.fromServer(String(parseInt(weight[1].trim(), 10))) : null;
      this.localCargoUnit.palletType = CargoUnitsService.getPalletTypeListForSelect()[0];
      this.localCargoUnit.qty        = qty ? Qty.fromServer(String(parseInt(qty[1].trim(), 10))) : null;

      this.imported = true;
    },
  },
});
</script>
